import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Field, Input, Spinner } from './ui';
import { Icon } from './icons';

/**
 * Buying the company a phone number.
 *
 * ## Why an area code and not a list
 *
 * A contractor does not want "a number". They want one that looks local to
 * the people they serve, because a 512 number answered in Austin gets picked
 * up and an 833 does not. So the first question is the only one that matters,
 * and it is three digits they already know by heart.
 *
 * ## Why buying is deliberately awkward
 *
 * It is a monthly charge that starts immediately and a number that cannot be
 * un-bought. The confirm step exists because the list is a grid of similar
 * digits and the difference between two rows is invisible — exactly the shape
 * of a misclick somebody pays for every month until they notice.
 */
export function PhoneCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [areaCode, setAreaCode] = useState('');
  const [searched, setSearched] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: phone, isLoading } = useQuery({
    queryKey: ['phone'],
    queryFn: () => api.phone.status(),
  });

  const search = useQuery({
    queryKey: ['phone', 'search', searched],
    queryFn: () => api.phone.search(searched!),
    enabled: Boolean(searched),
  });

  const buy = useMutation({
    mutationFn: (number: string) => api.phone.buy(number),
    onSuccess: () => {
      setConfirming(null);
      setSearched(null);
      void qc.invalidateQueries({ queryKey: ['phone'] });
    },
    onError: (e) => {
      setConfirming(null);
      setError((e as { message?: string }).message ?? 'Could not buy that number');
    },
  });

  if (isLoading) {
    return (
      <Card title="Phone">
        <div className="grid place-items-center py-6">
          <Spinner className="size-5 text-ink-4" />
        </div>
      </Card>
    );
  }

  /*
   * Nothing offered when the platform has no Twilio credentials. A buy button
   * that always fails is worse than no button: it reads as the product being
   * broken rather than as a feature that is not switched on.
   */
  if (!phone?.configured) {
    return (
      <Card title="Phone">
        <p className="text-[13px] text-ink-3">
          Phone lines are not available on this account yet.
        </p>
      </Card>
    );
  }

  if (phone.number) {
    return (
      <Card title="Phone">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-accent-soft text-accent">
            <Icon name="phone" size={19} />
          </span>
          <div>
            <div className="font-display text-[18px] font-extrabold tracking-[-0.02em] text-ink">
              {formatNumber(phone.number)}
            </div>
            <div className="text-[12.5px] text-ink-3">
              Calls ring every office browser that has Crewlark open.
            </div>
          </div>
        </div>

        <ForwardingField current={phone.forwardTo} canManage={canManage} />

        {/*
         * Said plainly rather than left to be discovered. A contractor who
         * assumes they can text from their new number and finds out when a
         * customer does not reply is a support call and a lost afternoon.
         */}
        {!phone.canText && (
          <p className="mt-4 rounded-lg border border-line bg-raised px-3 py-2.5 text-[13px] text-ink-3">
            <span className="font-semibold text-ink">Calls only for now.</span> Texting from this
            number needs a separate registration with the US carriers, which takes a few days. We
            will let you know when it is ready.
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card title="Phone">
      <p className="text-[13px] text-ink-3">
        Get a local number for the business. Calls ring in the browser, and every one is logged
        against the customer who made it.
      </p>

      {error && (
        <p className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
      )}

      {canManage ? (
        <>
          <div className="mt-4 flex items-end gap-2">
            <Field label="Area code" hint="Three digits — 512, 713">
              <Input
                value={areaCode}
                inputMode="numeric"
                maxLength={3}
                placeholder="512"
                onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                className="w-[110px]"
              />
            </Field>
            <Button
              variant="secondary"
              disabled={areaCode.length !== 3}
              loading={search.isFetching}
              onClick={() => {
                setError(null);
                setSearched(areaCode);
              }}
            >
              Find numbers
            </Button>
          </div>

          {search.isError && (
            <p className="mt-3 text-[13px] text-danger">
              {(search.error as { message?: string } | null)?.message ??
                'Could not search that area code.'}
            </p>
          )}

          {search.data && search.data.items.length === 0 && (
            <p className="mt-3 text-[13px] text-ink-3">
              No numbers free in {searched}. Try a neighbouring area code.
            </p>
          )}

          {search.data && search.data.items.length > 0 && (
            <ul className="mt-4 divide-y divide-line rounded-xl border border-line">
              {search.data.items.map((n) => (
                <li key={n.number} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div>
                    <div className="tnum font-semibold text-ink">{formatNumber(n.number)}</div>
                    {(n.locality || n.region) && (
                      <div className="text-[12.5px] text-ink-4">
                        {[n.locality, n.region].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  {confirming === n.number ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[12.5px] text-ink-3">Sure?</span>
                      <Button size="sm" onClick={() => setConfirming(null)}>
                        No
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={buy.isPending}
                        onClick={() => buy.mutate(n.number)}
                      >
                        Buy it
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setConfirming(n.number)}>
                      Choose
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="mt-3 text-[13px] text-ink-3">An admin can set this up.</p>
      )}
    </Card>
  );
}

/**
 * Where a call goes when nobody answers in the browser.
 *
 * Usually the owner's own mobile, which is why it is its own field and not
 * the company phone: that one prints on every invoice. Empty is allowed and
 * means an unanswered caller is apologised to, which some offices prefer to
 * a phone ringing on a job site.
 */
function ForwardingField({ current, canManage }: { current: string | null; canManage: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(current ? formatNumber(current) : '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (forwardTo: string | null) => api.phone.setForwarding(forwardTo),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['phone'] });
    },
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not save that number'),
  });

  const parsed = value.trim() ? toE164(value) : null;
  const invalid = value.trim() !== '' && parsed === null;
  const unchanged = parsed === current;

  if (!canManage) {
    return (
      <p className="mt-4 text-[13px] text-ink-3">
        {current
          ? `Unanswered calls forward to ${formatNumber(current)}.`
          : 'Unanswered calls are not forwarded.'}
      </p>
    );
  }

  return (
    <form
      className="mt-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid) save.mutate(parsed);
      }}
    >
      <div className="flex items-end gap-2">
        <Field
          label="If nobody answers, forward to"
          hint="optional"
          error={invalid ? 'Enter a full phone number with area code.' : error}
          className="flex-1"
        >
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="(512) 555-0142"
            inputMode="tel"
          />
        </Field>
        <Button type="submit" disabled={invalid || unchanged} loading={save.isPending}>
          Save
        </Button>
      </div>
      <p className="mt-1.5 text-[12px] text-ink-4">
        Rings for 20 seconds once the browsers have. Left empty, the caller hears that nobody is
        available.
      </p>
    </form>
  );
}

/** What a person types, as E.164 — or null when it cannot be one. */
function toE164(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  if (input.trim().startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/** +15125550142 as (512) 555-0142 — how a contractor reads a number back. */
function formatNumber(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  if (digits.length !== 11 || !digits.startsWith('1')) return e164;
  return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
}
